import { ApiError, OfflineError } from "@/api/baseApi";
import { ZodError } from "zod";
import { HttpStatus } from "@/types/http-status.types";
import { captureException } from "@/utils/sentry";

interface ErrorData {
  message?: string;
  reasons?: string[];
}

export interface ErrorMessages {
  unauthorized: string;
  forbidden: string;
  notFound: string;
  serverUnavailable: string;
  offline: string;
  validationError: string;
  generic: string;
}

const TECHNICAL_KEYWORDS = [
  'invalid_type',
  'expected',
  'received',
  'undefined',
  'null',
  'parse',
  'schema'
];

function extractApiErrorMessage(apiError: ApiError): string | null {
  if (!apiError.data || typeof apiError.data !== 'object') {
    return null;
  }

  const errorData = Array.isArray(apiError.data)
    ? (apiError.data[0] as ErrorData | undefined)
    : (apiError.data as ErrorData);

  if (!errorData) {
    return null;
  }

  if (errorData.message) {
    return errorData.message;
  }
  
  if (errorData.reasons && Array.isArray(errorData.reasons) && errorData.reasons.length > 0) {
    return errorData.reasons.join(', ');
  }
  
  return null;
}

function getHttpStatusMessage(status: number, messages: ErrorMessages): string {
  if (status === HttpStatus.UNAUTHORIZED) {
    return messages.unauthorized;
  }
  if (status === HttpStatus.FORBIDDEN) {
    return messages.forbidden;
  }
  if (status === HttpStatus.NOT_FOUND) {
    return messages.notFound;
  }
  if (status >= HttpStatus.INTERNAL_SERVER_ERROR) {
    return messages.serverUnavailable;
  }
  
  return messages.generic;
}

function handleApiError(apiError: ApiError, messages: ErrorMessages): string {
  const extractedMessage = extractApiErrorMessage(apiError);
  if (extractedMessage) {
    return extractedMessage;
  }
  
  return getHttpStatusMessage(apiError.status, messages);
}

function handleOfflineError(messages: ErrorMessages): string {
  return messages.offline;
}

function handleZodError(zodError: ZodError, messages: ErrorMessages): string {
  captureException(zodError, {
    extra: {
      errorType: 'ZodValidationError',
      issues: zodError.issues,
      formattedError: zodError.format(),
    },
  });
  return messages.validationError;
}

function hasTechnicalKeywords(message: string): boolean {
  return TECHNICAL_KEYWORDS.some(keyword => 
    message.toLowerCase().includes(keyword)
  );
}

function handleGenericError(error: Error, messages: ErrorMessages): string {
  if (!hasTechnicalKeywords(error.message)) {
    return error.message;
  }
  
  captureException(error, {
    extra: {
      errorType: 'TechnicalError',
      message: error.message,
    },
  });
  
  return messages.generic;
}

function handleStringError(error: string): string {
  return error;
}

export function getUserFriendlyErrorMessage(
  error: unknown,
  messages: ErrorMessages
): string {
  if (error instanceof ApiError) {
    return handleApiError(error, messages);
  }
  
  if (error instanceof OfflineError) {
    return handleOfflineError(messages);
  }
  
  if (error instanceof ZodError) {
    return handleZodError(error, messages);
  }
  
  if (error instanceof Error) {
    return handleGenericError(error, messages);
  }
  
  if (typeof error === 'string') {
    return handleStringError(error);
  }
  
  return messages.generic;
}
